/**
 * Street Music — generative Web Audio loops that change per neighborhood.
 */
const ZONES = {
  club: { label: 'CLUB STREET', bpm: 116, wave: 'sine', vol: 0.045, bass: 55, bassEvery: 4, notes: [220, null, null, null, 220, null, 262, null, 220, null, null, null, 220, null, 196, null] },
  neon: { label: 'NEON ROW', bpm: 124, wave: 'sawtooth', vol: 0.03, bass: 110, bassEvery: 4, notes: [220, 262, 330, 440, 330, 262, 220, 262, 330, 440, 523, 440, 330, 262, 220, 196] },
  palm: { label: 'PALM COURT', bpm: 100, wave: 'triangle', vol: 0.06, bass: 131, bassEvery: 8, notes: [262, null, 330, 392, null, 330, 262, null, 392, null, 523, null, 392, 330, null, null] },
  autumn: { label: 'AUTUMN LANE', bpm: 84, wave: 'sine', vol: 0.06, bass: 98, bassEvery: 8, notes: [196, null, null, 247, null, null, 294, null, null, 247, null, null, 392, null, null, null] },
  snow: { label: 'SNOWFALL PINES', bpm: 72, wave: 'sine', vol: 0.05, bass: null, bassEvery: 0, bell: true, notes: [523, null, null, null, 659, null, null, null, 784, null, null, null, 1047, null, 659, null] },
  rich: { label: 'MILLIONAIRE ROW', bpm: 92, wave: 'triangle', vol: 0.05, bass: 117, bassEvery: 4, notes: [233, null, 294, null, 349, null, 440, null, 466, null, 440, null, 349, null, 294, null] },
  admin: { label: 'ADMIN AVENUE', bpm: 88, wave: 'triangle', vol: 0.05, bass: 82, bassEvery: 4, notes: [196, null, 233, null, 262, null, 233, null, 196, null, 175, null, 196, null, 262, null] },
  fair: { label: 'FAIRGROUNDS', bpm: 132, wave: 'square', vol: 0.022, bass: 87, bassEvery: 4, notes: [392, 523, 659, 523, 698, 659, 523, 392, 440, 587, 698, 587, 784, 698, 587, 440] },
};

export class StreetMusic {
  constructor() {
    this.ctx = null;
    this.master = null;
    this.enabled = true;
    this.started = false;
    this.zone = null;
    this.timer = null;
    this.step = 0;
  }

  ensureStarted() {
    if (this.started || !this.enabled) return;
    try {
      this.ctx = new (window.AudioContext || window.webkitAudioContext)();
      this.master = this.ctx.createGain();
      this.master.gain.value = 1;
      this.master.connect(this.ctx.destination);
      this.started = true;
      this._schedule();
    } catch (e) { /* audio unsupported */ }
  }

  _schedule() {
    if (this.timer) { clearInterval(this.timer); this.timer = null; }
    const z = ZONES[this.zone];
    if (!z || !this.started || !this.enabled) return;
    const stepMs = 60000 / z.bpm / 2;
    this.timer = setInterval(() => this._tick(), stepMs);
  }

  _tick() {
    const z = ZONES[this.zone];
    if (!z || !this.ctx) return;
    const i = this.step % 16;
    this.step++;
    const f = z.notes[i];
    if (f) this._note(f, z.wave, z.vol, z.bell ? 1.1 : 0.28);
    if (z.bass && z.bassEvery && i % z.bassEvery === 0) this._note(z.bass, 'sine', 0.075, 0.3);
  }

  _note(freq, type, vol, dur) {
    try {
      const ctx = this.ctx;
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.connect(g);
      g.connect(this.master);
      o.frequency.value = freq;
      o.type = type;
      g.gain.setValueAtTime(vol, ctx.currentTime);
      g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + dur);
      o.start(ctx.currentTime);
      o.stop(ctx.currentTime + dur + 0.02);
    } catch (e) { /* noop */ }
  }

  setZone(zone) {
    if (zone === this.zone) return;
    this.zone = zone;
    this.step = 0;
    this._schedule();
  }

  setEnabled(on) {
    this.enabled = on;
    if (!on) {
      if (this.timer) { clearInterval(this.timer); this.timer = null; }
    } else {
      this.ensureStarted();
      this._schedule();
    }
  }

  dispose() {
    if (this.timer) clearInterval(this.timer);
    try { this.ctx?.close(); } catch (e) { /* noop */ }
  }
}

export const zoneForY = (y) => {
  if (y < 400) return 'club';
  if (y < 1040) return 'neon';
  if (y < 1680) return 'palm';
  if (y < 2320) return 'autumn';
  if (y < 2960) return 'snow';
  if (y < 4150) return 'rich';
  if (y < 4620) return 'admin';
  return 'fair';
};

export const zoneLabel = (z) => ZONES[z]?.label || '';
